<template>
  <div class="analytics-container">
    <DashboardHeader />
    
    <FilterSection
      :filters="filters"
      :filter-options="filterOptions"
      :filtered-rows-count="filteredRows.length"
      @update-filter="updateFilter"
      @reset-filters="resetFilters"
    />

    <KpiGrid
      v-if="stats"
      :stats="stats"
      :dashboard-cards="dashboardCards"
    />

    <ChartsSection
      :participants-by-date="participantsByDate"
      :participants-by-cruise="participantsByCruise"
      :chart-data-by-date="chartDataByDate"
      :chart-data-by-cruise="chartDataByCruise"
      :chart-options-by-date="chartOptionsByDate"
      :chart-options-by-cruise="chartOptionsByCruise"
      :selected-filters="selectedFilters"
    />

    <DataTable
      :filtered-rows="filteredRows"
      :file-data-flat="fileDataFlat"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

// Components
import DashboardHeader from './components/DashboardHeader.vue'
import FilterSection from './components/FilterSection.vue'
import KpiGrid from './components/KpiGrid.vue'
import ChartsSection from './components/ChartsSection.vue'
import DataTable from './components/DataTable.vue'

// Utils
import { transformJsonToRows } from './utils/dataTransformers'
import { computeStatistics } from './utils/statistics'
import { 
  generateDashboardCards,
  createDashboardCards 
} from './utils/chartUtils'
import { 
  getFilterOptions,
  setupFilterWatchers,
  getFilteredRows 
} from './utils/filterUtils'

// Data
import tourJson from './utils/all_tours.json'

// Reactive state
const fileDataFlat = ref(transformJsonToRows(tourJson || {}))
const stats = ref(null)

// Filters
const filters = ref({
  year: '2026',
  port: '',
  tour: '',
  cruise: '',
  date: '',
  time: '',
  ship: ''
})

// Computed
const filteredRows = computed(() => 
  getFilteredRows(fileDataFlat.value, filters.value)
)

const filterOptions = computed(() =>
  getFilterOptions(fileDataFlat.value, filters.value)
)

const selectedFilters = computed(() => filters.value)

// Statistics and Charts
const participantsByDate = computed(() => 
  filteredRows.value.reduce((acc, row) => {
    acc[row.date] = (acc[row.date] || 0) + row.participants
    return acc
  }, {})
)

const participantsByCruise = computed(() => 
  filteredRows.value.reduce((acc, row) => {
    acc[row.cruiseLine] = (acc[row.cruiseLine] || 0) + row.participants
    return acc
  }, {})
)

// FIX: Properly compute dashboardCards
const dashboardCards = computed(() => {
  return createDashboardCards(stats.value)
})

const {
  chartDataByDate,
  chartOptionsByDate,
  chartDataByCruise,
  chartOptionsByCruise
} = generateDashboardCards(
  participantsByDate,
  participantsByCruise,
  filteredRows,
  selectedFilters
)

// Methods
const updateFilter = (filterName, value) => {
  filters.value[filterName] = value
}

const resetFilters = () => {
  filters.value = {
    year: '2026',
    port: '',
    tour: '',
    cruise: '',
    date: '',
    time: '',
    ship: ''
  }
}

// Watchers
setupFilterWatchers(filters, fileDataFlat)

// Initial computation and watchers
watch(
  [() => filters.value, fileDataFlat],
  () => {
    stats.value = computeStatistics(filteredRows.value)
  },
  { immediate: true }
)
</script>

<style scoped>
.analytics-container {
  max-width: 1580px;
  margin: 0 auto;
  padding: 1rem;
  font-family: 'Inter', sans-serif;
  background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%);
  color: #1f2937;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 90vh;
}
</style>